import { toast } from 'react-toastify';
import { API } from './Api';
import {
  downloadAuthBlobResponse,
  getBlobErrorMessage,
  parseFilenameFromContentDisposition,
} from './Utils';

/**
 * Secure document / export access — see docs/FE_SECURE_DOCUMENT_ACCESS.md
 * Pattern A: export blobs (JWT + responseType blob)
 * Pattern B: documents/serve/... proxy (JWT + blob open)
 */

const DOCUMENT_SERVE_PREFIX = 'documents/serve/';

/**
 * Pull documents/serve/... out of a relative path or a full API URL.
 * Example: https://host/documents/serve/xml_reporting/88 → documents/serve/xml_reporting/88
 */
export const extractDocumentServePath = (value) => {
  if (!value || typeof value !== 'string') return '';
  const match = value.trim().match(/documents\/serve\/[^\s"'<>]+/i);
  if (!match) return '';
  return match[0].replace(/^\//, '');
};

/** True when API returned an authenticated document proxy path. */
export const isDocumentServeRef = (value) => Boolean(extractDocumentServePath(value));

/** Legacy public S3 absolute URL — do not open (bucket is private). */
export const isLegacyS3Url = (value) => {
  if (!value || typeof value !== 'string') return false;
  const href = value.trim().toLowerCase();
  return href.startsWith('http') && href.includes('amazonaws.com');
};

/** Absolute http(s) document URL that is not a documents/serve path. */
export const isLegacyDocumentUrl = (value) => {
  if (!value || typeof value !== 'string') return false;
  const text = value.trim();
  if (!text || extractDocumentServePath(text)) return false;
  return /^https?:\/\//i.test(text);
};

/** Normalize proxy path for axios (relative to API baseURL). */
export const normalizeDocumentRef = (documentRef) => extractDocumentServePath(documentRef);

/**
 * GET documents/serve/... with JWT as blob; open in new tab (preview)
 * or force download when options.download === true.
 */
export const openDocument = async (documentRef, options = {}) => {
  const { download = false, fallbackFilename = 'document' } = options;
  const path = normalizeDocumentRef(documentRef);
  if (!path || !isDocumentServeRef(path)) {
    throw new Error('Invalid document reference');
  }

  const url = download ? `${path}${path.includes('?') ? '&' : '?'}download=1` : path;
  let res;
  try {
    res = await API.get(url, { responseType: 'blob' });
  } catch (error) {
    throw new Error(await getBlobErrorMessage(error, 'Document unavailable'));
  }
  const type = String(res.headers?.['content-type'] || res.data?.type || '').toLowerCase();

  if (type.includes('application/json') || type.includes('text/json')) {
    const text = await res.data.text();
    let message = 'Document unavailable';
    try {
      const json = JSON.parse(text);
      message = json?.message || message;
    } catch (e) {
      // keep default
    }
    throw new Error(message);
  }

  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data], { type: type || 'application/octet-stream' });
  const objectUrl = window.URL.createObjectURL(blob);

  if (download) {
    const filename = parseFilenameFromContentDisposition(
      res.headers?.['content-disposition'],
      fallbackFilename
    );
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60_000);
    return true;
  }

  window.open(objectUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 120_000);
  return true;
};

/**
 * Open any document field value from list/detail APIs:
 * - documents/serve/... → authenticated blob open
 * - amazonaws.com → refuse (legacy / broken)
 * - empty → false
 */
export const openSecureDocument = async (value, options = {}) => {
  const path = extractDocumentServePath(value);
  if (path) {
    await openDocument(path, options);
    return true;
  }

  if (typeof value === 'string' && isLegacyS3Url(value)) {
    throw new Error('Document unavailable');
  }

  return false;
};

/** Toast-friendly wrapper for UI click handlers. */
export const openSecureDocumentOrToast = async (value, options = {}) => {
  try {
    const ok = await openSecureDocument(value, options);
    if (!ok) {
      toast.error('Document unavailable', {
        position: toast.POSITION.TOP_CENTER,
      });
    }
    return ok;
  } catch (error) {
    toast.error(error?.message || 'Document unavailable', {
      position: toast.POSITION.TOP_CENTER,
    });
    return false;
  }
};

/**
 * Resolve a PPA PDF open target.
 * Prefer documents/serve proxy; if list still returns legacy S3/empty, build proxy from ppa id.
 */
export const resolvePpaDocumentRef = (ppaId, ppaUrl) => {
  if (typeof ppaUrl === 'string' && isDocumentServeRef(ppaUrl)) {
    return normalizeDocumentRef(ppaUrl);
  }
  if (ppaId != null && ppaId !== '') {
    return `documents/serve/ppa/${ppaId}`;
  }
  return '';
};

/**
 * Open PPA PDF via authenticated proxy (after optional download_ppa mark API).
 */
export const openPpaDocumentOrToast = async (ppaId, ppaUrl, options = {}) => {
  const ref = resolvePpaDocumentRef(ppaId, ppaUrl);
  return openSecureDocumentOrToast(ref, {
    download: true,
    fallbackFilename: `ppa_${ppaId || 'download'}.pdf`,
    ...options,
  });
};

/**
 * Resolve Utilization Certificate PDF — prefer proxy ref; else build from row id.
 * Resource: documents/serve/utilization_certificate/{id}
 */
export const resolveUcDocumentRef = (ucId, s3UrlOrRef) => {
  if (typeof s3UrlOrRef === 'string' && isDocumentServeRef(s3UrlOrRef)) {
    return normalizeDocumentRef(s3UrlOrRef);
  }
  if (ucId != null && ucId !== '') {
    return `documents/serve/utilization_certificate/${ucId}`;
  }
  return '';
};

export const openUcDocumentOrToast = async (ucId, s3UrlOrRef, options = {}) => {
  const ref = resolveUcDocumentRef(ucId, s3UrlOrRef);
  return openSecureDocumentOrToast(ref, {
    download: true,
    fallbackFilename: `utilization_certificate_${ucId || 'download'}.pdf`,
    ...options,
  });
};

/**
 * Try proxy refs in order. Used when a list still returns an S3 URL
 * but the file can be opened via documents/serve/{resource}/{id}.
 */
export const openFirstDocumentOrToast = async (candidates, options = {}) => {
  const refs = (candidates || [])
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => isDocumentServeRef(value));

  if (!refs.length) {
    toast.error('Document unavailable', {
      position: toast.POSITION.TOP_CENTER,
    });
    return false;
  }

  let lastMessage = 'Document unavailable';
  for (const ref of refs) {
    try {
      await openDocument(ref, options);
      return true;
    } catch (error) {
      lastMessage = error?.message || lastMessage;
    }
  }

  toast.error(lastMessage, {
    position: toast.POSITION.TOP_CENTER,
  });
  return false;
};

/**
 * Child allocation order / supporting file.
 * List rows often store a relative path or an old absolute URL. A plain
 * anchor navigates the SPA and never calls the API. Prefer a documents/serve
 * value when the API already returned one; otherwise GET by allocation id.
 */
export const openAllocateBudgetDocument = (allocationId, field, storedValue) => {
  const stored = typeof storedValue === 'string' ? storedValue.trim() : '';
  if (extractDocumentServePath(stored)) {
    return openSecureDocumentOrToast(stored, { fallbackFilename: field });
  }
  if (!allocationId || !field) {
    toast.error('Document unavailable', {
      position: toast.POSITION.TOP_CENTER,
    });
    return Promise.resolve(false);
  }
  return openSecureDocumentOrToast(
    `documents/serve/allocate_budget/${allocationId}/${field}`,
    { fallbackFilename: field }
  );
};

export const saveExportBlob = downloadAuthBlobResponse;
export const exportBlobErrorMessage = getBlobErrorMessage;
